import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import SingleSelect from '../../components/SingleSelect';
import TypeAhead, { type TypeAheadOption } from '../../components/TypeAhead';
import LookupResultCard, { type LookupResultCardProps } from '../order-management/components/LookupResultCard';
import { TanTable } from '../../components/TanTable';
import { useAdditionalFieldColumns, useAdditionalFieldSelection } from '../../hooks/useAdditionalFields';
import { locationOptionLabel } from '../../components/LocationTypeAhead';
import {
  downloadContainerStatusWorkbook, getContainerStatusOptions, runContainerStatusReport,
  type ContainerStatusFilters, type ContainerStatusOptions, type ContainerStatusResult, type ContainerStatusRow,
} from '../../services/containerStatusReport.service';
import { reportError, REPORT_BUTTON } from '../accounting/reports/reportUtils';
import { formatCurrency } from '../../utils/money';
import { getApiFieldName } from '../../utils/dynamicColumns';

const EMPTY_FORM = { delivery: '', billto: '', customer: '', broker: '', cmscode: '', movetype: '' };
type FilterKey = keyof typeof EMPTY_FORM;
const FIELDS: { key: FilterKey; label: string }[] = [
  { key: 'delivery', label: 'Delivery Location' }, { key: 'billto', label: 'Bill To' },
  { key: 'customer', label: 'Customer' }, { key: 'broker', label: 'Broker' },
  { key: 'cmscode', label: 'CMS Code' }, { key: 'movetype', label: 'Move Type' },
];
const lookupOption = (
  name: string, recno: number,
  row: { address?: string; address2?: string; city?: string; state?: string; zip?: string },
  details: LookupResultCardProps['details'] = [],
): TypeAheadOption => ({
  value: String(recno), label: name, labelSuffix: `#${recno}`,
  description: [row.address, row.address2, row.city, row.state, row.zip, ...details.map(d => d.value)].filter(Boolean).join(', '),
  data: { name, code: recno, address1: row.address, address2: row.address2, city: row.city, state: row.state, zip: row.zip, details } satisfies LookupResultCardProps,
});

export default function ContainerStatusReport() {
  const fieldSelection = useAdditionalFieldSelection('container-status-report');
  const [options, setOptions] = useState<ContainerStatusOptions | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [result, setResult] = useState<ContainerStatusResult | null>(null);
  const [active, setActive] = useState('');
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadOptions = useCallback(async () => {
    setOptionsLoading(true); setOptionsError(null);
    try { setOptions(await getContainerStatusOptions()); }
    catch { setOptionsError('Unable to load report filters. Please try again.'); }
    finally { setOptionsLoading(false); }
  }, []);
  useEffect(() => { void loadOptions(); }, [loadOptions]);
  const choices = useMemo<Record<FilterKey, TypeAheadOption[]>>(() => ({
    delivery: (options?.locations ?? []).map(r => lookupOption(locationOptionLabel(r.lname, r.cyalias), r.recno, r)),
    billto: (options?.billtos ?? []).map(r => lookupOption(r.cname, r.recno, r, [
      { label: 'SAGE', value: r.mas90code }, { label: 'Terms', value: r.creditterms },
      { label: 'AR', value: Number(r.arbalance || 0).toFixed(2) },
      { label: 'Credit Hold', value: r.credithold === 'Y' ? 'Yes' : '', danger: true },
    ])),
    customer: (options?.customers ?? []).map(r => lookupOption(r.custname, r.recno, r, [
      { label: 'Sales Rep', value: r.salesrep }, { label: 'Key Acct', value: r.keyaccount },
    ])),
    broker: (options?.brokers ?? []).map(r => lookupOption(r.name, r.recno, r)),
    cmscode: (options?.cmscodes ?? []).map(value => ({ value, label: value })),
    movetype: (options?.movetypes ?? []).filter(Boolean).map(value => ({ value, label: value })),
  }), [options]);
  const tab = result?.tabs.find(t => t.key === active);
  const baseColumns = useMemo<ColumnDef<ContainerStatusRow>[]>(() => (tab?.columns ?? []).filter(col => !col.additional).map(col => ({
    accessorKey: col.key, header: col.label, size: Math.max(110, Math.min(360, col.width * 9)),
    meta: { registryTitle: false, ...(col.format === 'date' && col.key !== 'firstrcvdate' ? { isDate: true, hideMidnightTime: true } : {}) },
    ...(col.format === 'dec2' ? { cell: ({ getValue }) => formatCurrency(getValue()) } : {}),
  })), [tab]);
  // A shared added-field selection applies to all statuses. Some statuses already
  // show a selected field by default; keep that base column instead of adding it twice.
  const columnSelection = useMemo(() => {
    const shown = new Set((tab?.columns ?? []).filter(c => !c.additional).flatMap(c => [c.key, getApiFieldName(c.key)]));
    if (shown.has('delivertoname')) shown.add('destname');
    return { ...fieldSelection, additionalFields: fieldSelection.additionalFields.filter(name => !shown.has(name) && !shown.has(getApiFieldName(name))) };
  }, [fieldSelection, tab]);
  const { columns, availableFields } = useAdditionalFieldColumns<ContainerStatusRow>(columnSelection, {
    baseColumns, coveredFields: { d_arrivaldatetime: ['d_arrivaldatetime'], delivertoname: ['destname'] },
  });
  const fetchReport = async (params: ContainerStatusFilters, fields: string[], background = false) => {
    setLoading(true); setResult(null); setError(null); setSaved(false);
    try {
      const report = await runContainerStatusReport({ ...params,
        ...(fields.length ? { additional_fields: fields.join(',') } : {}), ...(background ? { background: true } : {}),
      });
      setResult(report);
      setActive(background && report.tabs.some(t => t.key === active) ? active :
        report.tabs.find(t => t.rows.length)?.key ?? report.tabs[0]?.key ?? '');
    } catch (e) { setError(reportError(e, 'Unable to run the container status report. Please try again.')); }
    finally { setLoading(false); }
  };
  const changeAdditionalFields = (fields: string[]) => {
    fieldSelection.onAdditionalFieldsChange(fields);
    if (result) void fetchReport(result.filters, fields, true);
  };
  const run = async (event: React.FormEvent) => {
    event.preventDefault(); setSaved(false); setError(null);
    if (![form.delivery, form.billto, form.customer, form.broker, form.cmscode].some(Boolean)) {
      setError('Select at least one of Delivery Location, Bill To, Customer, Broker, or CMS Code.'); return;
    }
    const params: ContainerStatusFilters = {
      ...(form.delivery ? { delivery: Number(form.delivery) } : {}),
      ...(form.billto ? { billto: Number(form.billto) } : {}),
      ...(form.customer ? { customer: Number(form.customer) } : {}),
      ...(form.broker ? { broker: Number(form.broker) } : {}), cmscode: form.cmscode, movetype: form.movetype,
    };
    await fetchReport(params, fieldSelection.additionalFields);
  };
  const download = async () => {
    if (!result || downloading) return;
    setDownloading(true); setError(null); setSaved(false);
    try { setSaved(await downloadContainerStatusWorkbook({ ...result.filters, ...(fieldSelection.additionalFields.length ? { additional_fields: fieldSelection.additionalFields.join(',') } : {}) })); }
    catch (e) { setError(reportError(e, 'Unable to download the workbook. Please try again.')); }
    finally { setDownloading(false); }
  };
  const busy = loading || downloading;
  return <div className="p-4 space-y-4 text-label">
    <h1 className="text-xl font-semibold">Container Status Report</h1>
    <form onSubmit={run} className="rounded-lg border border-theme-border-primary bg-theme-bg-primary p-4 space-y-4">
      <p className="text-sm text-muted">Select at least one location, bill to, customer, broker, or CMS code. Customer includes its divisions.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {FIELDS.map(({ key, label }) => <div key={key}>
          <div className="text-sm font-medium mb-1">{label}</div>
          {key === 'cmscode' || key === 'movetype' ? <SingleSelect ariaLabel={label} value={form[key]} options={choices[key]} enableSearch showClearOption clearOptionLabel="All"
            placeholder="All" loading={optionsLoading} disabled={busy || optionsLoading || !options}
            onChange={value => setForm(prev => ({ ...prev, [key]: value }))} /> :
            <TypeAhead ariaLabel={label} value={form[key]} options={choices[key]} placeholder="All"
              disabled={busy || optionsLoading || !options}
              onChange={value => setForm(prev => ({ ...prev, [key]: value }))}
              renderOption={option => <LookupResultCard {...option.data as LookupResultCardProps} />} />}

        </div>)}
      </div>
      {optionsError && <div><p role="alert" className="text-sm text-red-600 dark:text-red-400">{optionsError}</p>
        <button type="button" onClick={() => void loadOptions()} className="text-sm underline">Retry Filters</button></div>}
      <div className="flex flex-wrap gap-3">
        <button type="submit" disabled={busy || optionsLoading || !options || !fieldSelection.ready} className={REPORT_BUTTON}>{loading ? 'Running…' : 'Run Report'}</button>
        <button type="button" disabled={busy} onClick={() => { setForm(EMPTY_FORM); setResult(null); setError(null); setSaved(false); }}
          className="rounded border border-theme-border-primary px-4 py-2 text-sm disabled:opacity-50">Clear</button>
      </div>
    </form>
    {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    {saved && <p role="status" className="text-sm">Workbook saved.</p>}
    {result && <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="text-sm font-medium">{result.criteria}</p><p className="text-sm text-muted">{result.total.toLocaleString()} containers</p></div>
        <button onClick={() => void download()} disabled={busy} className={REPORT_BUTTON}>{downloading ? 'Downloading…' : 'Download Excel Workbook'}</button>
      </div>
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Container status">
        {result.tabs.map(t => <button key={t.key} role="tab" aria-selected={active === t.key} aria-controls="container-status-panel"
          id={`container-status-tab-${t.key}`} onClick={() => setActive(t.key)}
          className={`rounded border px-3 py-2 text-sm ${active === t.key ? 'bg-blue-600 border-blue-600 text-white' : 'border-theme-border-primary hover:bg-theme-bg-secondary'}`}>
          {t.label} ({t.rows.length.toLocaleString()})
        </button>)}
      </div>
      {tab && <div role="tabpanel" id="container-status-panel" aria-labelledby={`container-status-tab-${tab.key}`}>
        {tab.rows.length === 0 ? <p className="text-sm text-muted">No containers in this status.</p> : <div className="h-[65vh] min-h-80">
          <TanTable<ContainerStatusRow> key={tab.key} data={tab.rows} columns={columns} cardTitle={tab.label}
            settingsKey={`container-status-report-${tab.key}-${tab.columns.filter(c => !c.additional).map(c => c.key).join('-')}`}
            searchPlaceholder="Search containers..." enableGrouping={false} fillContainer enableColumnFilters
            onExcelExport={download} excelExportLoading={downloading}
            availableFields={availableFields} additionalFields={fieldSelection.additionalFields}
            onAdditionalFieldsChange={changeAdditionalFields} />
        </div>}
      </div>}
    </>}
  </div>;
}
